import { useMemo, useState } from "react";
import { clsx } from "clsx";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { formatAmount } from "@/utils/formatAmount";
import {
  useCatalogEntries,
  useCatalogStats,
  useCatalogs,
  useNormCatalogs,
  useNormFamilies,
  useNormPosition,
  useNormPositions,
  useNormTables,
} from "../hooks";
import type {
  NormFamily,
  NormItem,
  NormPosition,
  RateCatalog,
} from "../types";

const PAGE_SIZE = 50;

const KIND_LABELS: Record<string, string> = {
  labor: "R — robocizna",
  material: "M — materiał",
  equipment: "S — sprzęt",
  work_unit: "Robota (cena jednostkowa)",
};

const KIND_BADGE: Record<string, "blue" | "green" | "orange" | "purple"> = {
  labor: "blue",
  material: "green",
  equipment: "orange",
  work_unit: "purple",
};

const KIND_SHORT: Record<string, string> = {
  labor: "R",
  material: "M",
  equipment: "S",
  work_unit: "RB",
};

function money(v: string | null | undefined): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n) ? v : formatAmount(n);
}

/** Norma bywa zapisana z sześcioma miejscami po przecinku ("2.240000") — zera na
 *  końcu tylko zaśmiecają tabelę, ale samej wartości NIE zaokrąglamy. */
function norm(v: string): string {
  const trimmed = v.includes(".") ? v.replace(/0+$/, "").replace(/\.$/, "") : v;
  return trimmed.replace(".", ",");
}

const inputCls =
  "border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1.5 " +
  "text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)]";

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 py-6 text-sm text-[var(--color-text-muted)] text-center">
      {children}
    </p>
  );
}

function Loading() {
  return (
    <div className="flex justify-center py-8">
      <Spinner />
    </div>
  );
}

/** Pasek stronicowania — pokazuje PEŁNĄ liczbę trafień, nie rozmiar okna. */
function Pager({
  page,
  count,
  onPage,
}: {
  page: number;
  count: number;
  onPage: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  if (count === 0) return null;
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-[var(--color-border)] text-xs text-[var(--color-text-secondary)]">
      <span>
        {count.toLocaleString("pl-PL")}{" "}
        {count === 1 ? "pozycja" : "pozycji"} · strona {page} z {pages}
      </span>
      <span className="flex gap-1">
        <button
          className="px-2 py-1 border border-[var(--color-border)] disabled:opacity-40 hover:border-[var(--color-primary)]"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          ‹ poprzednia
        </button>
        <button
          className="px-2 py-1 border border-[var(--color-border)] disabled:opacity-40 hover:border-[var(--color-primary)]"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          następna ›
        </button>
      </span>
    </div>
  );
}

// ── Zakładka 1: normy KNR/KNNR ───────────────────────────────────────────────

/** Lewa kolumna: rodziny katalogów zwinięte po prefiksie (KNR, KNNR, KNP…). */
function FamilyTree({
  families,
  selected,
  onSelect,
}: {
  families: NormFamily[];
  selected: string | null;
  onSelect: (code: string) => void;
}) {
  const groups = useMemo(() => {
    const by = new Map<string, NormFamily[]>();
    families.forEach((f) => {
      const list = by.get(f.group) ?? [];
      list.push(f);
      by.set(f.group, list);
    });
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0], "pl"));
  }, [families]);

  const [open, setOpen] = useState<Record<string, boolean>>({});

  return (
    <div className="divide-y divide-[var(--color-border)]">
      {groups.map(([group, items]) => {
        const positions = items.reduce((s, f) => s + f.positions, 0);
        const isOpen = open[group] ?? items.some((f) => f.catalog_code === selected);
        return (
          <div key={group}>
            <button
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-[var(--color-bg-base)]"
              onClick={() => setOpen((s) => ({ ...s, [group]: !isOpen }))}
            >
              <span className="font-mono text-xs font-semibold text-[var(--color-text-primary)]">
                {isOpen ? "▾" : "▸"} {group}
              </span>
              <span className="text-[11px] text-[var(--color-text-muted)] tabular-nums">
                {items.length} ks. · {positions.toLocaleString("pl-PL")}
              </span>
            </button>
            {isOpen &&
              items.map((f) => (
                <button
                  key={f.catalog_code}
                  onClick={() => onSelect(f.catalog_code)}
                  className={clsx(
                    "w-full flex items-center justify-between gap-2 pl-6 pr-3 py-1.5 text-left text-sm",
                    f.catalog_code === selected
                      ? "bg-[var(--color-primary-dim)] text-[var(--color-primary)] font-medium"
                      : "hover:bg-[var(--color-bg-base)] text-[var(--color-text-secondary)]",
                  )}
                >
                  <span className="font-mono text-xs truncate">{f.catalog_code}</span>
                  <span className="text-[11px] text-[var(--color-text-muted)] tabular-nums whitespace-nowrap">
                    {f.tables} tabl.
                    {f.coded === 0 && (
                      <span title="Ta rodzina nie ma nadanych kodów — pozycję identyfikuje tablica i numer wariantu.">
                        {" "}
                        · bez kodów
                      </span>
                    )}
                  </span>
                </button>
              ))}
          </div>
        );
      })}
    </div>
  );
}

/** Szczegół wariantu: nakłady R/M/S + rodzeństwo tablicy + cena z BCJ. */
function VariantDetail({ id, onPick }: { id: string; onPick: (id: string) => void }) {
  const q = useNormPosition(id);
  if (q.isLoading) return <Loading />;
  const d = q.data;
  if (!d) return <Empty>Nie udało się wczytać wariantu.</Empty>;

  const byKind = (k: NormItem["kind"]) => d.items.filter((i) => i.kind === k);

  return (
    <div className="space-y-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          {d.normalized_code ? (
            <Badge color="purple">{d.normalized_code}</Badge>
          ) : (
            <Badge color="gray">wariant {d.variant_index}</Badge>
          )}
          <span className="font-mono text-xs text-[var(--color-text-muted)]">
            {d.catalog_code}
          </span>
          {d.soil_category && <Badge color="orange">kat. {d.soil_category}</Badge>}
        </div>
        <p className="mt-2 text-sm text-[var(--color-text-primary)]">{d.description}</p>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          Tablica: {d.table_title}
          {d.kat_file && <> · plik {d.kat_file}</>}
        </p>
      </div>

      {d.bcj_prices.length > 0 && (
        <div className="border border-[var(--color-border)] p-3">
          <p className="text-xs font-medium text-[var(--color-text-secondary)] mb-2">
            Cena jednostkowa roboty z cennika BCJ dla tego kodu
          </p>
          {d.bcj_prices.map((p) => (
            <div key={p.catalog_id} className="flex flex-wrap items-baseline gap-x-3 text-sm">
              <span className="font-mono text-xs text-[var(--color-text-muted)]">
                {p.catalog_label}
                {p.quarter && ` (${p.quarter})`}
              </span>
              <span className="tabular-nums">
                {money(p.price_min)} / <strong>{money(p.price_avg)}</strong> /{" "}
                {money(p.price_max)}
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                zł / {p.unit || "jm"} (min / śr. / maks)
              </span>
            </div>
          ))}
        </div>
      )}

      <div>
        <p className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">
          Nakłady na jednostkę pozycji ({d.items.length})
        </p>
        {d.items.length === 0 ? (
          <Empty>Ten wariant nie ma zapisanych nakładów.</Empty>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                <th className="py-1 pr-2 font-medium w-8">R/M/S</th>
                <th className="py-1 pr-2 font-medium">Czynnik</th>
                <th className="py-1 pr-2 font-medium w-16">Jm</th>
                <th className="py-1 font-medium text-right w-28">Norma</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {(["labor", "material", "equipment"] as const).flatMap((k) =>
                byKind(k).map((i) => (
                  <tr key={i.id}>
                    <td className="py-1 pr-2">
                      <Badge color={KIND_BADGE[i.kind]}>{KIND_SHORT[i.kind]}</Badge>
                    </td>
                    <td className="py-1 pr-2">
                      {i.description}
                      {i.is_percent_aux && (
                        <span
                          className="ml-1 text-[11px] text-[var(--color-text-muted)]"
                          title="Norma jest procentem od wartości materiałów, nie ilością."
                        >
                          (% od M)
                        </span>
                      )}
                    </td>
                    <td className="py-1 pr-2 text-xs text-[var(--color-text-secondary)]">
                      {i.unit || "—"}
                    </td>
                    <td className="py-1 text-right tabular-nums font-medium">
                      {norm(i.norm)}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        )}
      </div>

      {d.variants.length > 1 && (
        <div>
          <p className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">
            Warianty tej tablicy ({d.variants.length})
          </p>
          <div className="divide-y divide-[var(--color-border)] border border-[var(--color-border)]">
            {d.variants.map((v) => (
              <button
                key={v.id}
                onClick={() => onPick(v.id)}
                className={clsx(
                  "w-full text-left px-2 py-1.5 text-xs flex items-start gap-2",
                  v.is_current
                    ? "bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                    : "hover:bg-[var(--color-bg-base)]",
                )}
              >
                <span className="font-mono tabular-nums w-6 shrink-0">
                  {v.variant_index}.
                </span>
                <span className="flex-1">{v.description}</span>
                {v.soil_category && (
                  <span className="shrink-0 text-[var(--color-text-muted)]">
                    kat. {v.soil_category}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NormsTab() {
  const [family, setFamily] = useState<string | null>(null);
  const [table, setTable] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [tableFilter, setTableFilter] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const searching = search.trim().length >= 3;
  const catalogs = useNormCatalogs();
  const families = useNormFamilies();
  const tables = useNormTables(searching ? null : family);

  // Szukanie działa PONAD drzewem: wpisany tekst przeszukuje wszystkie rodziny,
  // więc nie trzeba wiedzieć, w której księdze siedzi szukana tablica.
  const positions = useNormPositions(
    searching
      ? { q: search.trim(), page, page_size: PAGE_SIZE }
      : { family: family ?? "", table: table ?? "", page, page_size: PAGE_SIZE },
    searching || Boolean(table),
  );

  // Filtr tablic działa na już pobranej liście księgi — bez zapytania na literę.
  const shownTables = useMemo(() => {
    const needle = tableFilter.trim().toLowerCase();
    const rows = tables.data ?? [];
    if (!needle) return rows;
    return rows.filter(
      (t) =>
        t.table_title.toLowerCase().includes(needle) ||
        t.table_code.includes(needle),
    );
  }, [tables.data, tableFilter]);

  const reset = (fn: () => void) => {
    fn();
    setPage(1);
    setOpenId(null);
  };

  const base = catalogs.data?.[0];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => reset(() => setSearch(e.target.value))}
          placeholder="Szukaj w całym katalogu: kod (KNNR 5 0701) albo opis (kopanie rowów dla kabli)…"
          className={clsx(inputCls, "flex-1 min-w-[280px]")}
        />
        {searching && (
          <button
            className="text-xs text-[var(--color-primary)] hover:underline"
            onClick={() => reset(() => setSearch(""))}
          >
            wyczyść i wróć do drzewa
          </button>
        )}
        {base && (
          <span className="text-[11px] text-[var(--color-text-muted)]">
            {base.source_label} · {base.position_count.toLocaleString("pl-PL")} wariantów
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)_minmax(0,1.2fr)] gap-3 items-start">
        {/* 1. rodziny */}
        <div className="card overflow-hidden max-h-[70vh] overflow-y-auto">
          <p className="px-3 py-2 text-[11px] uppercase tracking-wide text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
            Katalogi
          </p>
          {families.isLoading ? (
            <Loading />
          ) : (
            <FamilyTree
              families={families.data ?? []}
              selected={family}
              onSelect={(code) =>
                reset(() => {
                  setSearch("");
                  setFamily(code);
                  setTable(null);
                  setTableFilter("");
                })
              }
            />
          )}
        </div>

        {/* 2. tablice rodziny albo wyniki szukania */}
        <div className="card overflow-hidden max-h-[70vh] flex flex-col">
          {searching ? (
            <>
              <p className="px-3 py-2 text-[11px] uppercase tracking-wide text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                Wyniki szukania
              </p>
              <div className="flex-1 overflow-y-auto">
                {positions.isLoading ? (
                  <Loading />
                ) : (positions.data?.results.length ?? 0) === 0 ? (
                  <Empty>Nic nie znaleziono. Spróbuj samego numeru tablicy albo innego słowa z opisu.</Empty>
                ) : (
                  <PositionList
                    rows={positions.data!.results}
                    openId={openId}
                    onOpen={setOpenId}
                    showFamily
                  />
                )}
              </div>
              <Pager page={page} count={positions.data?.count ?? 0} onPage={setPage} />
            </>
          ) : (
            <>
              <div className="px-3 py-2 border-b border-[var(--color-border)] flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                  Tablice
                </span>
                {family && (
                  <input
                    value={tableFilter}
                    onChange={(e) => setTableFilter(e.target.value)}
                    placeholder="filtruj tablice…"
                    className={clsx(inputCls, "flex-1 py-0.5 text-xs")}
                  />
                )}
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-[var(--color-border)]">
                {!family ? (
                  <Empty>Wybierz katalog z lewej albo wpisz szukaną frazę u góry.</Empty>
                ) : tables.isLoading ? (
                  <Loading />
                ) : shownTables.length === 0 ? (
                  <Empty>Brak tablic dla tego filtra.</Empty>
                ) : (
                  shownTables.map((t) => (
                    <button
                      key={`${t.catalog_code}|${t.table_title}`}
                      onClick={() => reset(() => setTable(t.table_title))}
                      className={clsx(
                        "w-full text-left px-3 py-2 flex items-start gap-2",
                        t.table_title === table
                          ? "bg-[var(--color-primary-dim)]"
                          : "hover:bg-[var(--color-bg-base)]",
                      )}
                    >
                      {t.table_code && (
                        <span className="font-mono text-xs text-[var(--color-text-muted)] shrink-0 pt-0.5">
                          {t.table_code}
                        </span>
                      )}
                      <span className="flex-1 text-sm text-[var(--color-text-primary)]">
                        {t.table_title}
                      </span>
                      <span className="text-[11px] text-[var(--color-text-muted)] tabular-nums shrink-0 pt-0.5">
                        {t.variants} war.
                      </span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* 3. warianty tablicy + szczegół */}
        <div className="card overflow-hidden max-h-[70vh] flex flex-col">
          <p className="px-3 py-2 text-[11px] uppercase tracking-wide text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
            {openId ? "Wariant i nakłady" : "Warianty tablicy"}
          </p>
          <div className="flex-1 overflow-y-auto">
            {openId ? (
              <div className="p-3">
                <button
                  className="text-xs text-[var(--color-primary)] hover:underline mb-3"
                  onClick={() => setOpenId(null)}
                >
                  ← wróć do listy wariantów
                </button>
                <VariantDetail id={openId} onPick={setOpenId} />
              </div>
            ) : searching ? (
              <Empty>Kliknij wynik z listy obok, żeby zobaczyć nakłady R/M/S.</Empty>
            ) : !table ? (
              <Empty>Wybierz tablicę, żeby zobaczyć jej warianty.</Empty>
            ) : positions.isLoading ? (
              <Loading />
            ) : (
              <PositionList
                rows={positions.data?.results ?? []}
                openId={openId}
                onOpen={setOpenId}
              />
            )}
          </div>
          {!openId && !searching && table && (
            <Pager page={page} count={positions.data?.count ?? 0} onPage={setPage} />
          )}
        </div>
      </div>
    </div>
  );
}

function PositionList({
  rows,
  openId,
  onOpen,
  showFamily = false,
}: {
  rows: NormPosition[];
  openId: string | null;
  onOpen: (id: string) => void;
  showFamily?: boolean;
}) {
  return (
    <div className="divide-y divide-[var(--color-border)]">
      {rows.map((p) => (
        <button
          key={p.id}
          onClick={() => onOpen(p.id)}
          className={clsx(
            "w-full text-left px-3 py-2",
            p.id === openId
              ? "bg-[var(--color-primary-dim)]"
              : "hover:bg-[var(--color-bg-base)]",
          )}
        >
          <div className="flex items-center gap-2 flex-wrap">
            {p.normalized_code ? (
              <span className="font-mono text-[11px] text-[var(--color-primary)]">
                {p.normalized_code}
              </span>
            ) : (
              <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                wariant {p.variant_index}
              </span>
            )}
            {showFamily && (
              <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                {p.catalog_code}
              </span>
            )}
            {p.soil_category && (
              <span className="text-[11px] text-[var(--color-text-muted)]">
                kat. {p.soil_category}
              </span>
            )}
            <span className="ml-auto text-[11px] text-[var(--color-text-muted)] tabular-nums">
              {p.item_count} nakł.
            </span>
          </div>
          <p className="text-sm text-[var(--color-text-primary)] mt-0.5">
            {p.description}
          </p>
        </button>
      ))}
    </div>
  );
}

// ── Zakładka 2: cenniki SEKOCENBUD ───────────────────────────────────────────

function CatalogStatsBar({ catalogId }: { catalogId: string }) {
  const stats = useCatalogStats(catalogId);
  if (!stats.data) return null;
  const { entry_count, by_kind, priced, with_range } = stats.data;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--color-text-secondary)]">
      <span>
        <strong className="tabular-nums">{entry_count.toLocaleString("pl-PL")}</strong>{" "}
        pozycji
      </span>
      {by_kind.map((k) => (
        <span key={k.kind}>
          {KIND_SHORT[k.kind] ?? k.kind}:{" "}
          <span className="tabular-nums">{k.count.toLocaleString("pl-PL")}</span>
        </span>
      ))}
      <span>
        z ceną: <span className="tabular-nums">{priced.toLocaleString("pl-PL")}</span>
      </span>
      <span title="Pozycje, które mają widełki min–maks, a nie samą cenę średnią.">
        z widełkami:{" "}
        <span className="tabular-nums">{with_range.toLocaleString("pl-PL")}</span>
      </span>
    </div>
  );
}

function PricesTab() {
  const catalogs = useCatalogs();
  const [catalogId, setCatalogId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [ordering, setOrdering] = useState("code");
  const [page, setPage] = useState(1);

  const list: RateCatalog[] = useMemo(
    () => (catalogs.data ?? []).filter((c) => !c.is_custom),
    [catalogs.data],
  );
  const active = catalogId ?? list[0]?.id ?? null;
  const entries = useCatalogEntries(active, {
    q: q.trim() || undefined,
    kind: kind || undefined,
    ordering,
    page,
    page_size: PAGE_SIZE,
  });

  const sortBtn = (key: string, label: string) => {
    const isActive = ordering === key || ordering === `-${key}`;
    return (
      <button
        className={clsx(
          "font-medium",
          isActive ? "text-[var(--color-primary)]" : "hover:underline",
        )}
        onClick={() => {
          setOrdering(ordering === key ? `-${key}` : key);
          setPage(1);
        }}
      >
        {label}
        {isActive && (ordering.startsWith("-") ? " ↓" : " ↑")}
      </button>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {catalogs.isLoading && <Loading />}
        {list.map((c) => (
          <button
            key={c.id}
            onClick={() => {
              setCatalogId(c.id);
              setPage(1);
            }}
            className={clsx(
              "px-3 py-2 border text-left",
              c.id === active
                ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                : "border-[var(--color-border)] hover:border-[var(--color-primary)]",
            )}
          >
            <div className="text-sm font-medium text-[var(--color-text-primary)]">
              {c.source_label}
            </div>
            <div className="text-[11px] text-[var(--color-text-muted)]">
              {c.kind === "bcj" ? "BCJ — ceny robót" : "RMS — ceny czynników"}
              {c.quarter && ` · ${c.quarter}`}
              {!c.is_active && " · nieaktywny"}
            </div>
          </button>
        ))}
        {!catalogs.isLoading && list.length === 0 && (
          <Empty>Nie wgrano jeszcze żadnego cennika SEKOCENBUD.</Empty>
        )}
      </div>

      {active && (
        <>
          <div className="card p-3 space-y-3">
            <CatalogStatsBar catalogId={active} />
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder="Szukaj po kodzie (KNNR 5 0701-02) albo nazwie (przewód YDY)…"
                className={clsx(inputCls, "flex-1 min-w-[260px]")}
              />
              <select
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value);
                  setPage(1);
                }}
                className={inputCls}
              >
                <option value="">wszystkie rodzaje</option>
                {Object.entries(KIND_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                    <th className="px-3 py-2">{sortBtn("code", "Kod")}</th>
                    <th className="px-3 py-2 w-12 font-medium">Rodzaj</th>
                    <th className="px-3 py-2">{sortBtn("description", "Nazwa")}</th>
                    <th className="px-3 py-2 font-medium w-16">Jm</th>
                    <th className="px-3 py-2 text-right w-28">
                      {sortBtn("price_min", "Cena min")}
                    </th>
                    <th className="px-3 py-2 text-right w-28">
                      {sortBtn("price_avg", "Cena śr.")}
                    </th>
                    <th className="px-3 py-2 text-right w-28">
                      {sortBtn("price_max", "Cena maks")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {entries.isLoading && (
                    <tr>
                      <td colSpan={7}>
                        <Loading />
                      </td>
                    </tr>
                  )}
                  {!entries.isLoading && (entries.data?.results.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <Empty>Brak pozycji dla tego filtra.</Empty>
                      </td>
                    </tr>
                  )}
                  {entries.data?.results.map((e) => (
                    <tr key={e.id} className="hover:bg-[var(--color-bg-base)]">
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                        {e.code || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Badge color={KIND_BADGE[e.kind] ?? "gray"}>
                          {KIND_SHORT[e.kind] ?? e.kind}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">{e.description}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">
                        {e.unit || "—"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                        {money(e.price_min)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium whitespace-nowrap">
                        {money(e.price_avg)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                        {money(e.price_max)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={page} count={entries.data?.count ?? 0} onPage={setPage} />
          </div>
        </>
      )}
    </div>
  );
}

/** Przeglądarka katalogów norm KNR/KNNR i cenników SEKOCENBUD — dokładnie te
 *  same dane, z których moduł wycen rozwija przedmiar na kosztorys nakładczy.
 *  Tylko do czytania: żeby sprawdzić, co katalog naprawdę mówi o pozycji
 *  (ile r-g, jaki materiał, jaka cena), bez otwierania wyceny. */
export function CatalogBrowserPage() {
  const [tab, setTab] = useState<"norms" | "prices">("norms");

  const tabCls = (t: typeof tab) =>
    clsx(
      "px-3 py-2 text-sm -mb-px border-b-2",
      tab === t
        ? "border-[var(--color-primary)] text-[var(--color-primary)] font-medium"
        : "border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]",
    );

  return (
    <div className="max-w-[1600px] mx-auto p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
          Katalogi i cenniki
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
          Normy KNR/KNNR (nakłady rzeczowe R/M/S) i cenniki SEKOCENBUD — ten sam zbiór,
          z którego wyceny rozwijają przedmiar na kosztorys nakładczy. Do podglądu:
          sprawdzisz tu, ile robocizny, materiału i sprzętu katalog przewiduje na
          jednostkę pozycji oraz po ile idzie dany czynnik, bez wchodzenia w konkretną
          wycenę. Zmian tu się nie robi.
        </p>
      </div>

      <div className="flex gap-1 border-b border-[var(--color-border)]">
        <button className={tabCls("norms")} onClick={() => setTab("norms")}>
          Normy KNR/KNNR
        </button>
        <button className={tabCls("prices")} onClick={() => setTab("prices")}>
          Cenniki SEKOCENBUD
        </button>
      </div>

      {tab === "norms" ? <NormsTab /> : <PricesTab />}
    </div>
  );
}
